import type { OnDestroy, OnInit } from '@angular/core';
import { ChangeDetectionStrategy, Component, input, ViewEncapsulation, inject, output } from '@angular/core';
import type { FormGroup } from '@angular/forms';
import { ControlContainer, FormGroupDirective, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { MatInput } from '@angular/material/input';
import { MatFormField, MatLabel, MatError } from '@angular/material/form-field';
import { AttentionComponent } from '@shared/consumption';
import { FuseAlertComponent } from '@ista-ui/angular';

export enum FormKeys {
  NEW_PASSWORD = 'newPassword',
  NEW_PASSWORD_CONFIRMATION = 'newPasswordConfirmation',
}

export enum ValidationKeys {
  REQUIRED = 'required',
  STRONG_PASSWORD = 'strongpassword',
  PASSWORDS_DONT_MATCH = 'passwordsDontMatch',
}

@Component({
  selector: 'ic-new-password-fields-section',
  templateUrl: './new-password-fields-section.component.html',
  viewProviders: [{ provide: ControlContainer, useExisting: FormGroupDirective }],
  styleUrls: ['./new-password-fields-section.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  imports: [
    MatFormField,
    FuseAlertComponent,
    MatLabel,
    MatInput,
    FormsModule,
    ReactiveFormsModule,
    MatError,
    TranslateModule,
    AttentionComponent,
  ],
})
export class NewPasswordFieldsSectionComponent implements OnInit, OnDestroy {
  // injectables
  private readonly formGroup = inject(FormGroupDirective);
  // inputs
  public readonly includeTooltip = input<boolean>(false);
  public readonly controlHasErrorFn = input<(controlName: FormKeys, validatorName?: string) => boolean>(undefined);
  // outputs
  public readonly changed = output<void>();
  // state
  public FormKeys = FormKeys;
  public ValidationKeys = ValidationKeys;

  public ngOnInit() {
    this.formGroup.form.addValidators([this.passwordsShouldMatch]);
  }

  public ngOnDestroy() {
    this.formGroup.form.removeValidators([this.passwordsShouldMatch]);
  }

  public formGroupHasError(validatorName?: string) {
    return !!this.formGroup.errors && (!validatorName || !!this.formGroup.errors[validatorName]);
  }

  public controlHasError(controlName: FormKeys, validatorName?: string) {
    const controlHasErrorFn = this.controlHasErrorFn();
    if (!controlHasErrorFn) {
      return false;
    }

    return controlHasErrorFn(controlName, validatorName);
  }

  private passwordsShouldMatch(group: FormGroup) {
    const { newPassword, newPasswordConfirmation } = group.getRawValue();

    if (!newPassword || !newPasswordConfirmation) {
      return null;
    }

    return newPassword === newPasswordConfirmation ? null : { [ValidationKeys.PASSWORDS_DONT_MATCH]: true };
  }
}
